import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';
import { Variables, AppManager, routerStore } from 'core/index';

import {
  GroupsApp,
  GroupModel,
  InviteLinkApp,
  InviteLinkModel,
} from 'apps/group/app';

import {
  GroupsQuery,
  InitiativeInvLinkQuery,
  ProjectInvLinkQuery,
  InitiativeSeatsQuery,
  ProfileSeatsQuery,
  RemoveOutsideCollaborator,
} from 'apps/group/gql';

export class GroupsAppManager extends AppManager {
  @observable isStarterPlan = false;

  @observable availableSeats = 0;

  @observable collaborators = [];

  @observable teamMembers = [];

  static registryName = 'GroupsAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('groups', GroupsApp);
    this.groups = observable([]);
    this.AppRef('inviteLink', InviteLinkApp);

    if (!this.params) {
      // TODO: fix in the actual caller for new GroupsAppManager
      this.params = routerStore.params;
    }

    this.variables = new Variables({
      spaceSlug: () =>
        this.params.get('profile') ||
        this.params.get('initiative') ||
        this.params.get('spacePath') ||
        `#${this.params.get('channel')}`,
      slug: () => this.params.get('slug') || '',
    });
    this.variables.onChange(['space', 'slug'], this.NewParams);
  }

  @computed
  get loading() {
    return (
      this.queries.get('groups')?.loading ||
      this.queries.get('inviteLink')?.loading
    );
  }

  @action
  NewParams = () => {
    this.NewGroupsQuery();
    this.NewLinkQuery();
  };

  @action
  NewGroupsQuery = () => {
    const groupsQ = new GroupsQuery(this, {
      queryKey: 'groups',
      onSysErr: this.onError,
    });
    groupsQ.process(groups => {
      if (groups !== null) {
        return groups.map(group => GroupModel.Load(group));
      }
      return [];
    }, this.GroupsUpdate);
    groupsQ.query();
  };

  @action
  RemoveOutsideCollaborator = async (input, callbacks) => {
    const removeOutsideCollaborator = new RemoveOutsideCollaborator(input);

    await removeOutsideCollaborator.mutate(callbacks);
  };

  @action
  QueryTeamGroup = spaceSlug => {
    const groupQuery = new GroupsQuery(this, {
      fetchPolicy: 'network-only',
      variables: {
        slug: '',
        spaceSlug,
      },
    });

    groupQuery.process(groups => {
      if (groups !== null) {
        this.teamMembers = groups.map(group => GroupModel.Load(group));
      }
      return groups;
    });

    groupQuery.query();
  };

  @computed
  get teamParticipants() {
    return this.teamMembers
      .map(group => {
        const { pendingInvites = [], members = [] } = group;

        return pendingInvites.concat(members);
      })
      .flatMap(members => {
        return members.map(item => ({
          displayName: item.profile?.username || item.email,
          ...item,
        }));
      });
  }

  @action isTeamAdmin = (currentProfile, teamMembers) => {
    if (!teamMembers?.length || !currentProfile) {
      return false;
    }

    const canOperateRoles = ['owner', 'admin'];

    const AdminOrOwnerMembers = teamMembers.filter(({ name }) =>
      canOperateRoles.some(r => r === name),
    );

    return AdminOrOwnerMembers.some(role => {
      const { members } = role;

      return members.some(m => m.profile?.id === currentProfile.id);
    });
  };

  @action getRepeatInvitationUsers = (invitedUsers, teamParticipants) => {
    const allParticipants = teamParticipants?.length
      ? teamParticipants
      : this.teamParticipants;

    if (!invitedUsers?.length || !allParticipants?.length) {
      return [];
    }

    const alreadyInvitedUsers = invitedUsers.filter(invitedUsernameOrEmail => {
      return allParticipants.some(
        p => p.displayName.trim() === invitedUsernameOrEmail.trim(),
      );
    });

    return alreadyInvitedUsers;
  };

  @action
  NewLinkQuery = () => {
    let spaceQ;
    if (this.variables.slug === '') {
      spaceQ = InitiativeInvLinkQuery;
    } else {
      spaceQ = ProjectInvLinkQuery;
    }
    // eslint-disable-next-line
    const inviteLinkQ = new spaceQ(this, {
      queryKey: 'inviteLink',
      onSysErr: this.onError,
    });
    inviteLinkQ.process(
      invLink => InviteLinkModel.Load(invLink),
      this.InvLinkUpdate,
    );
    inviteLinkQ.query();
  };

  @action
  onError = () => {
    Alert.error('Hmm... Something went wrong! Please try again.');
  };

  @action
  GroupsUpdate = groupModels => {
    this.groups = groupModels;
    this.groups.ResetLocals();
    // NOTE: Some logs used during debugging the "set locals" bug.
    // console.log('this.groups:', this.groups);
    // console.log(this.groups.map((group) => {
    //   return group.id;
    // }));
  };

  @action
  InviteMembers = () => {
    // NOTE: This mutation is currently on the GroupApp due to a bug.
    // Leaving this here as a reminder.
  };

  @action
  InvLinkUpdate = invLink => {
    if (invLink) {
      this.inviteLink = invLink;
    } else {
      this.inviteLink = new InviteLinkApp(this);
    }
    this.inviteLink.init();
  };

  @action
  ProfileQuery = username => {
    const Query = ProfileSeatsQuery;

    const profileSeatsQuery = new Query(this, {
      variables: {
        username,
      },
      fetchPolicy: 'network-only',
    });

    profileSeatsQuery.process(profile => {
      if (!profile) {
        return profile;
      }

      if (!profile.hasProSubscription) {
        this.isStarterPlan = true;
      }

      if (profile.collaborators) {
        this.collaborators = profile.collaborators;
      }

      this.availableSeats = profile.seatsUsage.unusedSeats;
    });

    profileSeatsQuery.query();
  };

  @action
  InitiativeQuery = slug => {
    const Query = InitiativeSeatsQuery;

    const initiativeSeatsQuery = new Query(this, {
      variables: {
        slug,
      },
      fetchPolicy: 'network-only',
    });

    initiativeSeatsQuery.process(initiative => {
      if (!initiative) {
        return null;
      }

      this.availableSeats = initiative.seatsUsage.unusedSeats;

      if (initiative.collaborators) {
        this.collaborators = initiative.collaborators;
      }
    });

    initiativeSeatsQuery.query();
  };
}
